import { useCallback, useEffect, useState } from 'react';
import { AppState } from 'react-native';

import { STRINGS } from '@/constants/strings';
import {
  authenticateBiometric,
  getBiometricKind,
  type BiometricKind,
} from '@/features/auth/services/biometric-auth';
import {
  clearBiometricCredential,
  saveBiometricCredential,
  type BiometricProvider,
} from '@/features/auth/services/biometric-credentials';
import { useBiometricStore } from '@/features/auth/stores/biometric-store';
import {
  clearPendingCredential,
  getPendingCredential,
} from '@/features/auth/utils/pending-credential';
import { getAuthUser } from '@/libs/firebase/auth/auth-helpers';
import { useSessionStore } from '@/stores/session-store';

/** Firebase `providerId` → our biometric provider tag. */
const mapProviderId = (providerId?: string): BiometricProvider => {
  if (providerId === 'google.com') return 'google';
  if (providerId === 'apple.com') return 'apple';
  return 'email';
};

/**
 * Drives the biometric opt-in. Reports the device's biometric kind (so the UI can hide the
 * option when there's none) and the persisted on/off flag.
 *
 * `enable` shows the OS biometric prompt first (Face ID / fingerprint) and only persists the
 * credential once the user passes it. The credential prefers what was captured at sign-in; if
 * that was lost (app killed before onboarding resumed) it falls back to the Firebase user —
 * provider stays correct, only the password is dropped (stored empty) per product decision.
 */
export function useBiometric() {
  const [kind, setKind] = useState<BiometricKind>(null);
  const accounts = useBiometricStore(s => s.accounts);
  const enableInStore = useBiometricStore(s => s.enable);
  const disableInStore = useBiometricStore(s => s.disable);

  // Scope the toggle to the signed-in account: it only reads as "on" when biometric is bound
  // to THIS account. Each account keeps its own binding (per-uid Keychain entry + store map),
  // so enabling/disabling on account A never affects account B.
  const currentUid = useSessionStore(s => s.user?.uid) ?? null;
  const enabled = currentUid != null && Boolean(accounts[currentUid]);

  // Resolve on mount and every time the app returns to the foreground — the user may
  // enroll/remove a fingerprint or Face ID in system Settings while we're backgrounded.
  useEffect(() => {
    getBiometricKind().then(setKind);
    const sub = AppState.addEventListener('change', state => {
      if (state === 'active') getBiometricKind().then(setKind);
    });
    return () => sub.remove();
  }, []);

  const enable = useCallback(async (): Promise<boolean> => {
    // Confirm identity with the OS biometric prompt before storing anything.
    const authenticated = await authenticateBiometric(STRINGS.onboarding.step4.faceIdPrompt);
    if (!authenticated) return false;

    const pending = getPendingCredential();
    const user = getAuthUser();

    const provider = pending?.provider ?? mapProviderId(user?.providerData?.[0]?.providerId);
    const email = pending?.email ?? user?.email ?? '';
    const password = pending?.password ?? '';
    const uid = user?.uid ?? '';
    // No uid means no signed-in account to bind the credential to — bail before storing.
    if (!uid) return false;

    const saved = await saveBiometricCredential(uid, { email, password, provider });
    if (!saved) return false;

    enableInStore({ uid, email, provider });
    clearPendingCredential();
    return true;
  }, [enableInStore]);

  const disable = useCallback(async (): Promise<void> => {
    if (!currentUid) return;
    await clearBiometricCredential(currentUid);
    disableInStore(currentUid);
  }, [currentUid, disableInStore]);

  return {
    /** Biometric kind for icon/copy, or null when the device has none enrolled/available. */
    kind,
    isSupported: kind !== null,
    /** Whether biometric login is currently turned on. */
    enabled,
    enable,
    disable,
  };
}

export default useBiometric;
